import { ChevronRight, Home, MoreHorizontal } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface BreadcrumbItem {
  label: string;
  href: string;
}

// Store navigation history in a module-level variable to persist across component re-renders
let navigationHistory: BreadcrumbItem[] = [];

export function Breadcrumb() {
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathRef = useRef<string>('');
  const isNavigatingViaBreadcrumbRef = useRef<boolean>(false);
  const [, forceUpdate] = useState({});

  // Map path segments to readable labels
  const pathLabels: Record<string, string> = {
    dashboard: 'Dashboard',
    assets: 'Assets',
    transactions: 'Transactions',
    additions: 'Additions',
    depreciation: 'Depreciation',
    revaluation: 'Revaluation',
    insights: 'Insights',
    enquiry: 'Asset History',
    audit: 'Audit Trail',
    checkinout: 'Check-In / Check-Out',
    calendar: 'Calendar',
    checkout: 'Check Out',
    settings: 'Settings',
    features: 'Feature Settings',
    compliance: 'Compliance',
    administration: 'Administration',
    imports: 'Import Assets',
    'import-transactions': 'Import Transactions',
    locations: 'Locations',
    categories: 'Categories',
    equipment: 'Equipment',
    'disposal-reasons': 'Disposal Reasons',
    'asset-options': 'Asset Options',
    help: 'Help & Support',
  };

  // Get label for current path
  const getCurrentPageLabel = (pathname: string): string => {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0 || pathname === '/dashboard') {
      return 'Dashboard';
    }
    
    const lastSegment = pathSegments[pathSegments.length - 1];
    return pathLabels[lastSegment] || lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  };

  // Update navigation history when location changes
  useEffect(() => {
    const currentPath = location.pathname;
    
    // If navigating via breadcrumb, we've already updated the history
    if (isNavigatingViaBreadcrumbRef.current) {
      isNavigatingViaBreadcrumbRef.current = false;
      previousPathRef.current = currentPath;
      return;
    }

    // Skip if it's the same path (e.g., refresh) and already in history
    if (previousPathRef.current === currentPath && 
        navigationHistory.length > 0 && 
        navigationHistory[navigationHistory.length - 1].href === currentPath) {
      return;
    }

    // Add current page to history
    const currentLabel = getCurrentPageLabel(currentPath);
    const newItem: BreadcrumbItem = {
      label: currentLabel,
      href: currentPath,
    };

    // Remove duplicate if it exists elsewhere in history
    navigationHistory = navigationHistory.filter(item => item.href !== currentPath);
    
    // Add to history (keep full history)
    navigationHistory.push(newItem);

    previousPathRef.current = currentPath;
    
    // Force re-render to show updated breadcrumb
    forceUpdate({});
  }, [location.pathname]);

  // Handle breadcrumb click
  const handleBreadcrumbClick = (href: string, index: number) => {
    isNavigatingViaBreadcrumbRef.current = true;
    
    // Find the actual index in the full history
    const clickedItem = navigationHistory.find(item => item.href === href);
    if (clickedItem) {
      const actualIndex = navigationHistory.indexOf(clickedItem);
      // Trim history to the clicked item
      navigationHistory = navigationHistory.slice(0, actualIndex + 1);
    }
    
    navigate(href);
  };

  // Handle ellipsis click (go to the item before the last 2 visible items)
  // If showing: Home > ... > D > E, clicking ... should go to C
  const handleEllipsisClick = () => {
    const historyWithoutDashboard = navigationHistory.filter(item => item.href !== '/dashboard');
    if (historyWithoutDashboard.length > 3) {
      // We're showing the last 2 items, so go to the 3rd from last (C in the example)
      const targetIndex = historyWithoutDashboard.length - 3;
      const targetItem = historyWithoutDashboard[targetIndex];
      if (targetItem) {
        handleBreadcrumbClick(targetItem.href, targetIndex);
      }
    }
  };

  // Build breadcrumb items to display
  const buildBreadcrumbItems = () => {
    const items: Array<BreadcrumbItem & { isEllipsis?: boolean; isHome?: boolean; isLast?: boolean }> = [];
    
    // Always show Home (Dashboard) first
    items.push({ label: 'Dashboard', href: '/dashboard', isHome: true });
    
    const currentPath = location.pathname;
    
    // If we're on dashboard, just show home
    if (currentPath === '/dashboard') {
      items[0].isLast = true;
      return items;
    }
    
    // Get the history excluding dashboard
    const historyWithoutDashboard = navigationHistory.filter(item => item.href !== '/dashboard');
    
    // If history has more than 3 items (excluding dashboard), show ellipsis
    // Show: Home > ... > [last 2 items] (D > E in your example)
    if (historyWithoutDashboard.length > 3) {
      items.push({ label: '...', href: '', isEllipsis: true });
      
      // Add last 2 items from history
      const lastTwo = historyWithoutDashboard.slice(-2);
      lastTwo.forEach((item, idx) => {
        items.push({
          ...item,
          isLast: idx === lastTwo.length - 1
        });
      });
    } else {
      // Show all history items
      historyWithoutDashboard.forEach((item, idx) => {
        items.push({
          ...item,
          isLast: idx === historyWithoutDashboard.length - 1
        });
      });
    }
    
    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();

  return (
    <nav aria-label="Breadcrumb" className="flex items-center space-x-1 text-sm text-muted-foreground">
      {breadcrumbItems.map((item, index) => {
        const isLast = item.isLast;
        const isHome = item.isHome;
        const isEllipsis = item.isEllipsis;
        
        return (
          <div key={`${item.href}-${index}`} className="flex items-center">
            {index > 0 && <ChevronRight className="h-4 w-4 mx-1 flex-shrink-0" />}
            
            {isEllipsis ? (
              <button
                onClick={handleEllipsisClick}
                className="hover:text-foreground transition-colors p-0 bg-transparent border-none cursor-pointer"
                title="Go back one more step"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            ) : isLast ? (
              <span className={cn(
                "font-medium text-foreground truncate max-w-[150px] sm:max-w-[200px] md:max-w-none",
                isHome && "flex items-center gap-2"
              )}>
                {isHome ? (
                  <>
                    <Home className="h-3.5 w-3.5" />
                    <span>Dashboard</span>
                  </>
                ) : (
                  item.label
                )}
              </span>
            ) : (
              <Link
                to={item.href}
                onClick={() => handleBreadcrumbClick(item.href, index)}
                className={cn(
                  'hover:text-foreground transition-colors',
                  isHome && 'flex items-center'
                )}
                title={isHome ? 'Dashboard' : item.label}
              >
                {isHome ? (
                  <Home className="h-3.5 w-3.5" />
                ) : (
                  <span className="truncate max-w-[150px] sm:max-w-[200px] md:max-w-none">
                    {item.label}
                  </span>
                )}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}
